import { useState, useEffect, useCallback } from 'react';
import {
  ConsultationData,
  getConsultationData,
  saveConsultationData,
  updateStepData,
  validateConsultationData,
  isConsultationComplete,
  initializeConsultationData,
  clearConsultationData,
} from '../utils/consultationStorage';

interface UseConsultationDataReturn {
  // Data
  consultationData: ConsultationData;
  currentStep: number;
  validation: { [key: string]: boolean };
  isComplete: boolean;
  
  // Actions
  setCurrentStep: (step: number) => void;
  updateBeneficiaryInfo: (data: ConsultationData['beneficiaryInfo']) => void;
  updateBeneficiarySituation: (data: ConsultationData['beneficiarySituation']) => void;
  updateConsultationType: (data: ConsultationData['consultationType']) => void;
  updateContextualData: (data: ConsultationData['contextualData']) => void;
  updateSelectedQuery: (data: ConsultationData['selectedQuery']) => void;
  initializeConsultation: (beneficiaryId: string, beneficiaryInfo: ConsultationData['beneficiaryInfo'], consultantInfo: {firstname: string, lastname: string}) => void;
  clearConsultation: () => void;
  
  // Navigation helpers
  canNavigateToStep: (step: number) => boolean;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
}

export const useConsultationData = (beneficiaryId?: string): UseConsultationDataReturn => {
  const [consultationData, setConsultationData] = useState<ConsultationData>(() => 
    getConsultationData() || {} as ConsultationData
  );

  // // Load data from sessionStorage on mount
  // useEffect(() => {
  //   const storedData = getConsultationData();
  //   if (beneficiaryId && storedData.beneficiaryId !== beneficiaryId) {
  //     // Initialize new consultation for different beneficiary
  //     const newData = initializeConsultationData(beneficiaryId, beneficiaryInfo);
  //     setConsultationData(newData);
  //   } else {
  //     setConsultationData(storedData);
  //   }
  // }, [beneficiaryId]);

  // Computed values
  const validation = validateConsultationData(consultationData);
  const isComplete = isConsultationComplete(consultationData);
  const { currentStep } = consultationData;

  // Update functions with automatic persistence
  const updateBeneficiaryInfo = useCallback((data: ConsultationData['beneficiaryInfo']) => {
    setConsultationData(prev => {
      const updated = { ...prev, beneficiaryInfo: data };
      saveConsultationData(updated);
      return updated;
    });
  }, []);

  const updateBeneficiarySituation = useCallback((data: ConsultationData['beneficiarySituation']) => {
    setConsultationData(prev => {
      const updated = { ...prev, beneficiarySituation: data };
      saveConsultationData(updated);
      return updated;
    });
  }, []);

  const updateConsultationType = useCallback((data: ConsultationData['consultationType']) => {
    setConsultationData(prev => {
      const updated = { ...prev, consultationType: data };
      saveConsultationData(updated);
      return updated;
    });
  }, []);

  const updateContextualData = useCallback((data: ConsultationData['contextualData']) => {
    setConsultationData(prev => {
      const updated = { ...prev, contextualData: data };
      saveConsultationData(updated);
      return updated;
    });
  }, []);

  const updateSelectedQuery = useCallback((data: ConsultationData['selectedQuery']) => {
    setConsultationData(prev => {
      const updated = { ...prev, selectedQuery: data };
      saveConsultationData(updated);
      return updated;
    });
  }, []);

  const setCurrentStep = useCallback((step: number) => {
    setConsultationData(prev => {
      const updated = { ...prev, currentStep: step };
      saveConsultationData(updated);
      return updated;
    });
  }, []);

  // Navigation helpers
  const canNavigateToStep = useCallback((step: number): boolean => {
    if (step <= 1) return true;
    if (step === 2) return true;
    if (step === 3) return validation.step2;
    if (step === 4) return validation.step2 && validation.step3;
    if (step === 5) return validation.step2 && validation.step3 && validation.step4;
    if (step === 6) return validation.step2 && validation.step3 && validation.step4 && validation.step5;
    return false;
  }, [validation]);

  const goToNextStep = useCallback(() => {
    const nextStep = Math.min(currentStep + 1, 6);
    if (canNavigateToStep(nextStep)) {
      setCurrentStep(nextStep);
    }
  }, [currentStep, canNavigateToStep, setCurrentStep]);

  const goToPreviousStep = useCallback(() => {
    const prevStep = Math.max(currentStep - 1, 1);
    setCurrentStep(prevStep);
  }, [currentStep, setCurrentStep]);

  const initializeConsultation = useCallback((beneficiaryIdParam: string, beneficiaryInfo: ConsultationData['beneficiaryInfo'], consultantInfo: {firstname: string, lastname: string}) => {

    const newData = initializeConsultationData(beneficiaryIdParam, beneficiaryInfo, consultantInfo);
    setConsultationData(newData);
  }, []);

  const clearConsultation = useCallback(() => {
    clearConsultationData();
    setConsultationData(getConsultationData() || {} as ConsultationData);
  }, []);

  return {
    // Data
    consultationData,
    currentStep,
    validation,
    isComplete,
    
    // Actions
    setCurrentStep,
    updateBeneficiaryInfo,
    updateBeneficiarySituation,
    updateConsultationType,
    updateContextualData,
    updateSelectedQuery,
    initializeConsultation,
    clearConsultation,
    
    // Navigation helpers
    canNavigateToStep,
    goToNextStep,
    goToPreviousStep,
  };
};
